import React from 'react'
import styled from 'styled-components'
import { DSText, DSSpacer, DSButton } from '@zoe/ds-web'
import { onNextClickType } from 'components/types'
import { ScreenContainer } from '../core/layout/ScreenContainer'
import { StickyFooter, StickyFooterContent } from '../core/layout/StickyFooter'
import { Quizzes } from '../../redux/quiz/types'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { TypewriterText } from '../core/text/TypewriterText'
import { ZiggieAvatar } from '../core/ziggie/ZiggieAvatar'
import { GifName } from '../../data/gifs'

export type QuizAnswerScreenProps = {
  questionKey: string
  correctAnswer: string
  correctTitle?: string
  incorrectTitle?: string
  correctIcon?: string
  incorrectIcon?: string
  description: string
  footer?: string
  // Same copy, shown with Ziggie: Ziggie's reaction to the answer, then the copy in
  // a speech bubble, in place of the icon.
  withZiggie?: { correctGif: GifName; incorrectGif: GifName; typewriter?: boolean }
}

type Props = QuizAnswerScreenProps & {
  onNextClick: onNextClickType
  quiz: Quizzes
}

const IconImg = styled.img.attrs({ loading: 'lazy' })`
  width: 40px;
  height: 40px;
`

const Reveal = styled.div<{ $shown: boolean }>`
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition: opacity 0.3s ease-out;
`

const ZiggieLayout = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const BUBBLE_COLOUR = 'var(--colour-surface-cream-bold)'

const Bubble = styled.div`
  position: relative;
  margin-top: var(--grid-16);
  padding: 20px;
  border-radius: 20px;
  background: ${BUBBLE_COLOUR};

  &::before {
    content: '';
    position: absolute;
    top: -11.5px;
    left: 22px;
    width: 20px;
    height: 13px;
    background: ${BUBBLE_COLOUR};
    clip-path: polygon(50% 0, 100% 100%, 0 100%);
  }
`

export const QuizAnswerScreen: React.FC<Props> = ({
  questionKey,
  correctAnswer,
  correctTitle,
  incorrectTitle,
  correctIcon,
  incorrectIcon,
  description,
  footer,
  withZiggie,
  onNextClick,
  quiz,
}) => {
  const userAnswer = String((quiz as Record<string, unknown>)[questionKey] ?? '')
  const isCorrect = userAnswer === correctAnswer

  const title = isCorrect ? (correctTitle ?? 'You\u2019re right!') : (incorrectTitle ?? 'Not quite')
  const icon = isCorrect ? correctIcon : incorrectIcon
  const typewriter = !!withZiggie?.typewriter
  const [typed, setTyped] = React.useState(!typewriter)

  // A tap while Ziggie is still typing finishes the text rather than skipping it.
  const onContinue = () => (typed ? onNextClick() : setTyped(true))

  const answer = (
    <>
      <DSText variant={withZiggie ? 'fluid-heading-600' : 'fluid-heading-700'} weight="semiBold" align="left">
        {title}
      </DSText>
      <DSSpacer size={16} direction="vertical" />
      <DSText
        variant={withZiggie ? 'fluid-paragraph-400' : 'fluid-paragraph-200'}
        color={withZiggie ? 'secondary' : undefined}
        as="div"
      >
        {typewriter ? (
          <TypewriterText text={description} quiz={quiz} complete={typed} onComplete={() => setTyped(true)} />
        ) : (
          <ContextAwareText text={description} quiz={quiz} />
        )}
      </DSText>
      {footer && (
        <Reveal $shown={typed}>
          <DSSpacer size={16} direction="vertical" />
          <DSText
            variant={withZiggie ? 'fluid-label-100' : 'fluid-paragraph-200'}
            color={withZiggie ? 'placeholder' : 'secondary'}
          >
            {footer}
          </DSText>
        </Reveal>
      )}
    </>
  )

  return (
    <>
      {withZiggie ? (
        <ZiggieLayout>
          <ScreenContainer>
            <ZiggieAvatar gif={isCorrect ? withZiggie.correctGif : withZiggie.incorrectGif} />
            <Bubble>{answer}</Bubble>
          </ScreenContainer>
        </ZiggieLayout>
      ) : (
        <ScreenContainer>
          {icon && (
            <>
              <DSSpacer size={24} direction="vertical" />
              <IconImg src={icon} alt="" />
              <DSSpacer size={16} direction="vertical" />
            </>
          )}
          {answer}
        </ScreenContainer>
      )}
      <StickyFooter>
        <StickyFooterContent>
          <DSButton
            variant="primary"
            colorScheme="default"
            size="large"
            width="full"
            onPress={onContinue}
            testId="continue-button"
          >
            <DSText variant="fluid-paragraph-200" weight="semiBold">
              Continue
            </DSText>
          </DSButton>
        </StickyFooterContent>
      </StickyFooter>
    </>
  )
}
